import Phaser from 'phaser';
import type { AudioManager } from '@/game/systems/AudioManager';
import { SONGS } from '@/data/songs';
import { CATS } from '@/data/cats';
import { FOODS, foodTextureKey } from '@/data/foods';
import { BACKGROUNDS, backgroundTextureKey } from '@/data/backgrounds';
import { catTextureKey, type CatPose } from '@/game/objects/Cat';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, bodyTextStyle, titleTextStyle } from '@/game/ui/theme';

const CAT_POSES: CatPose[] = ['idle', 'anticipate', 'eat'];

/**
 * Loads the real assets this project ships: AI-illustrated cat sprites and
 * background backdrops (Phaser's file loader, in `preload()`), and each bundled
 * song's audio, which -- since songs are procedurally synthesized rather than
 * fetched -- is rendered here via OfflineAudioContext instead of downloaded.
 * Progress against both phases drives one continuous bar rather than a fake spinner.
 */
export class PreloadScene extends Phaser.Scene {
  private fill!: Phaser.GameObjects.Graphics;
  private barX = 0;
  private barY = 0;
  private barW = 420;

  constructor() {
    super('PreloadScene');
  }

  preload(): void {
    this.add.text(INTERNAL_WIDTH / 2, INTERNAL_HEIGHT * 0.38, 'Meow Melody', titleTextStyle(52)).setOrigin(0.5);
    this.add
      .text(INTERNAL_WIDTH / 2, INTERNAL_HEIGHT * 0.38 + 60, 'warming up the kitchen...', bodyTextStyle(22, '#cbb9e8'))
      .setOrigin(0.5);

    this.barX = INTERNAL_WIDTH / 2 - this.barW / 2;
    this.barY = INTERNAL_HEIGHT * 0.55;
    const barBg = this.add.graphics();
    barBg.fillStyle(0x000000, 0.3);
    barBg.fillRoundedRect(this.barX, this.barY, this.barW, 18, 9);
    this.fill = this.add.graphics();

    for (const cat of CATS) {
      for (const pose of CAT_POSES) {
        this.load.image(catTextureKey(cat.id, pose), `images/cats/${cat.id}/${pose}.png`);
      }
    }
    for (const bg of BACKGROUNDS) {
      this.load.image(backgroundTextureKey(bg.id), `images/backgrounds/${bg.id}.jpg`);
    }
    for (const food of FOODS) {
      this.load.image(foodTextureKey(food.id), `images/foods/${food.id}.png`);
    }
    this.load.on(Phaser.Loader.Events.PROGRESS, (fraction: number) => this.setProgress(fraction * 0.5));
  }

  async create(): Promise<void> {
    const audioManager = this.game.registry.get('audioManager') as AudioManager;

    for (let i = 0; i < SONGS.length; i++) {
      await audioManager.loadSong(SONGS[i]);
      this.setProgress(0.5 + 0.5 * ((i + 1) / SONGS.length));
    }

    this.time.delayedCall(180, () => this.scene.start('HomeScene'));
  }

  private setProgress(fraction: number): void {
    this.fill.clear();
    this.fill.fillStyle(PALETTE.primary, 1);
    this.fill.fillRoundedRect(this.barX, this.barY, this.barW * fraction, 18, 9);
  }
}
